/* eslint-disable react/jsx-filename-extension */
import React from "react";
import { graphql } from "gatsby";
import { mergeCraftPreview, resolveImage, resolveCB } from "@dataResolvers";
import { Page as PageTemplate } from "@pageTemplates";

export const query = graphql`
  query ($slug: [String]) {
    craft {
      entry(section: "service", slug: $slug) {
        title
        ... on Craft_service_service_Entry {
          metaTitle
          metaDescription
          metaImage {
            width
            height
            hasFocalPoint
            slug
            status
            title
            focalPoint
            url
          }
          heroHeading
          image0 {
            width
            height
            hasFocalPoint
            slug
            status
            title
            focalPoint
            url
          }
          contentBuilder {
            ... on Craft_contentBuilder_copy_BlockType {
              uid
              type:typeHandle
              copy
            }
            ... on Craft_contentBuilder_centeredContentWColoredBackground_BlockType {
              type: typeHandle
              uid
              label
              labelColor
              heading
              headingColor
              description
              backgroundColor
              primaryButton {
                text
                url
              }
              secondaryButton {
                text
                url
              }
            }
            ... on Craft_contentBuilder_imageWithContent_BlockType {
              type: typeHandle
              uid
              label
              header
              description
              contentLink {
                text
                url
              }
              image {
                width
                height
                hasFocalPoint
                slug
                status
                title
                focalPoint
                url
              }
              shape
              shapeColor
              imagePosition
              circles
              bridge
              imageGrid {
                ... on Craft_imageGrid_BlockType {
                  image {
                    width
                    height
                    hasFocalPoint
                    slug
                    status
                    title
                    focalPoint
                    url
                  }
                  imageLink {
                    url
                  }
                }
              }
              showAndMore
              borderless
            }
            ... on Craft_contentBuilder_servicesWithHeading_BlockType {
              type: typeHandle
              uid
              horizontal
              heading
              headingColor
              subheading
              services {
                ... on Craft_services_BlockType {
                  heading
                  description
                  detailsLink {
                    text
                    url
                  }
                  color
                  icon
                }
              }
              serviceLink {
                text
                url
              }
              circles
              bridge
            }
            ... on Craft_contentBuilder_brandStatement_BlockType {
              type: typeHandle
              uid
              label
              heading
              description
              image {
                width
                height
                hasFocalPoint
                slug
                status
                title
                focalPoint
                url
              }
              portfolio {
                ... on Craft_portfolio_TableRow {
                  portfolioTitle
                  portfolioDescription
                }
              }
            }
            ... on Craft_contentBuilder_contentWithGradientBackground_BlockType {
              type: typeHandle
              uid
              label
              heading
              description
              list {
                ... on Craft_list_TableRow {
                  listItem
                }
              }
              image {
                width
                height
                hasFocalPoint
                slug
                status
                title
                focalPoint
                url
              }
              primaryButton {
                text
                url
              }
              secondaryButton {
                text
                url
              }
            }
            ... on Craft_contentBuilder_caseStudyCarousel_BlockType {
              type: typeHandle
              uid
              heading
              description
              caseStudies {
                ... on Craft_caseStudy_caseStudy_Entry {
                  title
                  url
                  descriptor0
                  hero:image0 {
                    width
                    height
                    hasFocalPoint
                    slug
                    status
                    title
                    focalPoint
                    url
                  }
                }
              }
            }
            ... on Craft_contentBuilder_quoteCarousel_BlockType {
              type: typeHandle
              uid
              quotes {
                ... on Craft_quotes_BlockType {
                  quote
                  name: quoteAuthor
                  details
                  image {
                    width
                    height
                    hasFocalPoint
                    slug
                    status
                    title
                    focalPoint
                    url
                  }
                }
              }
              bridge
            }
            ... on Craft_contentBuilder_columnedPortfolioWithHeadline_BlockType {
              type: typeHandle
              uid
              label
              heading
              description
              leftColumnHeading
              leftColumnPortfolio {
                ... on Craft_leftColumnPortfolio_TableRow {
                  portfolioTitle
                  portfolioDescription
                }
              }
              rightColumnHeading
              rightColumnPortfolio {
                ... on Craft_rightColumnPortfolio_TableRow {
                  portfolioTitle
                  portfolioDescription
                }
              }
              portfolioLink {
                text
                url
              }
            }
            ... on Craft_contentBuilder_portfolioWithHeadline_BlockType {
              type: typeHandle
              uid
              label
              heading
              description
              portfolio {
                ... on Craft_portfolio_BlockType {
                  portfolioTitle
                  portfolioDescription
                  portfolioUrl {
                    url
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const previewQuery = `
  query ($slug: [String]) {
    craft {
      entry(section: "service", slug: $slug) {
        title
        ... on Craft_service_service_Entry {
          metaTitle
          metaDescription
          metaImage {
            width
            height
            hasFocalPoint
            slug
            status
            title
            focalPoint
            url
          }
          heroHeading
          image0 {
            width
            height
            hasFocalPoint
            slug
            status
            title
            focalPoint
            url
          }
          contentBuilder {
            ... on Craft_contentBuilder_copy_BlockType {
              uid
              type:typeHandle
              copy
            }
            ... on Craft_contentBuilder_centeredContentWColoredBackground_BlockType {
              type: typeHandle
              uid
              label
              labelColor
              heading
              headingColor
              description
              backgroundColor
              primaryButton {
                text
                url
              }
              secondaryButton {
                text
                url
              }
            }
            ... on Craft_contentBuilder_imageWithContent_BlockType {
              type: typeHandle
              uid
              label
              header
              description
              contentLink {
                text
                url
              }
              image {
                width
                height
                hasFocalPoint
                slug
                status
                title
                focalPoint
                url
              }
              shape
              shapeColor
              imagePosition
              circles
              bridge
              imageGrid {
                ... on Craft_imageGrid_BlockType {
                  image {
                    width
                    height
                    hasFocalPoint
                    slug
                    status
                    title
                    focalPoint
                    url
                  }
                  imageLink {
                    url
                  }
                }
              }
              showAndMore
              borderless
            }
            ... on Craft_contentBuilder_servicesWithHeading_BlockType {
              type: typeHandle
              uid
              horizontal
              heading
              headingColor
              subheading
              services {
                ... on Craft_services_BlockType {
                  heading
                  description
                  detailsLink {
                    text
                    url
                  }
                  color
                  icon
                }
              }
              serviceLink {
                text
                url
              }
              circles
              bridge
            }
            ... on Craft_contentBuilder_brandStatement_BlockType {
              type: typeHandle
              uid
              label
              heading
              description
              image {
                width
                height
                hasFocalPoint
                slug
                status
                title
                focalPoint
                url
              }
              portfolio {
                ... on Craft_portfolio_TableRow {
                  portfolioTitle
                  portfolioDescription
                }
              }
            }
            ... on Craft_contentBuilder_contentWithGradientBackground_BlockType {
              type: typeHandle
              uid
              label
              heading
              description
              list {
                ... on Craft_list_TableRow {
                  listItem
                }
              }
              image {
                width
                height
                hasFocalPoint
                slug
                status
                title
                focalPoint
                url
              }
              primaryButton {
                text
                url
              }
              secondaryButton {
                text
                url
              }
            }
            ... on Craft_contentBuilder_caseStudyCarousel_BlockType {
              type: typeHandle
              uid
              heading
              description
              caseStudies {
                ... on Craft_caseStudy_caseStudy_Entry {
                  title
                  url
                  descriptor0
                  hero:image0 {
                    width
                    height
                    hasFocalPoint
                    slug
                    status
                    title
                    focalPoint
                    url
                  }
                }
              }
            }
            ... on Craft_contentBuilder_quoteCarousel_BlockType {
              type: typeHandle
              uid
              quotes {
                ... on Craft_quotes_BlockType {
                  quote
                  name: quoteAuthor
                  details
                  image {
                    width
                    height
                    hasFocalPoint
                    slug
                    status
                    title
                    focalPoint
                    url
                  }
                }
              }
              bridge
            }
            ... on Craft_contentBuilder_columnedPortfolioWithHeadline_BlockType {
              type: typeHandle
              uid
              label
              heading
              description
              leftColumnHeading
              leftColumnPortfolio {
                ... on Craft_leftColumnPortfolio_TableRow {
                  portfolioTitle
                  portfolioDescription
                }
              }
              rightColumnHeading
              rightColumnPortfolio {
                ... on Craft_rightColumnPortfolio_TableRow {
                  portfolioTitle
                  portfolioDescription
                }
              }
              portfolioLink {
                text
                url
              }
            }
            ... on Craft_contentBuilder_portfolioWithHeadline_BlockType {
              type: typeHandle
              uid
              label
              heading
              description
              portfolio {
                ... on Craft_portfolio_BlockType {
                  portfolioTitle
                  portfolioDescription
                  portfolioUrl {
                    url
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const dataResolver = ({ craft }) => {
  const {
    title,
    // Hero
    heroHeading,
    image0,
    // Content
    contentBuilder,
    // Meta
    metaTitle,
    metaDescription,
    metaImage,
  } = craft.entry;

  return {
    title,
    hero: {
      heading: heroHeading,
      image: resolveImage(image0),
      label: "Service",
      backgroundColor: "white",
      textColor: "red",
      shape: "roundedCorners",
      shapeColor: "teal",
    },
    content: resolveCB(contentBuilder),
    meta: {
      title,
      metaTitle,
      metaDescription,
      metaImage: resolveImage(metaImage),
    },
  };
};

const QueryContainer = ({ data, pageContext }) => {
  const { slug: _slug } = pageContext;
  const merged = mergeCraftPreview(data, previewQuery, {
    slug: _slug,
    site: process.env.GATSBY_CRAFT_SITE_HANDLE,
  });

  // When previewing unpublished pages, merged will be empty until
  // the preview reqeust has finished, so returning null to avoid
  // component errors about missing content.
  if (!merged) {
    return null;
  }

  const resolvedData = dataResolver(merged);

  return <PageTemplate data={resolvedData} />;
};

export default QueryContainer;
